import React, { useMemo } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    flexRender,
    ColumnDef,
} from '@tanstack/react-table';
import { Country } from '../types/Country';

interface Props {
    countries: Country[];
}

const CountryTable: React.FC<Props> = ({ countries }) => {
    const data = useMemo(() => countries, [countries]);

    const columns = useMemo<ColumnDef<Country>[]>(() => [
        {
            header: 'Country Name',
            accessorKey: 'name',
        },
        {
            header: 'Code',
            accessorKey: 'code',
        },
        {
            header: 'Capital',
            accessorKey: 'capital',
        },
        {
            header: 'Ph Code',
            accessorKey: 'phone',
        },
        {
            header: 'Population',
            accessorKey: 'population',
            cell: info => {
                const value = info.getValue<number | undefined | null>();
                return value != null ? value.toLocaleString() : '-';
            }
        },
        {
            header: 'Flag',
            accessorKey: 'flag',
            cell: info => (
                info.getValue<string>() ? (
                    <img
                        src={info.getValue<string>()}
                        alt={`${info.row.original.name} emblem`}
                        className="w-10 h-auto rounded shadow"
                    />
                ) : (
                    <span className="italic text-gray-400">N/A</span>
                )
            ),
        },
        {
            header: 'Emblem',
            accessorKey: 'emblem',
            cell: info => (
                info.getValue<string>() ? (
                    <img
                        src={info.getValue<string>()}
                        alt={`${info.row.original.name} emblem`}
                        className="w-10 h-auto rounded shadow"
                    />
                ) : (
                    <span className="italic text-gray-400">N/A</span>
                )
            ),
        },
    ], []);

    const [pagination, setPagination] = React.useState({ pageIndex: 0, pageSize: 10 });

    const table = useReactTable({
        data,
        columns,
        state: { pagination },
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getRowId: row => row.code
    });

    return (
        <>
            <table className="min-w-full bg-white shadow rounded overflow-hidden">
                <thead className="bg-gray-100">
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th
                                    key={header.id}
                                    className="text-left px-4 py-2 font-semibold"
                                >
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map(row => (
                        <tr key={row.id} className="hover:bg-gray-50">
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id} className="px-4 py-2 align-middle">
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="flex justify-end items-center mt-4 space-x-4">
                <button
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50"
                >
                    Previous
                </button>
                <span>
                    Page <strong>{table.getState().pagination.pageIndex + 1}</strong> of{' '}
                    {table.getPageCount()}
                </span>
                <button
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50"
                >
                    Next
                </button>
            </div>
        </>
    );
};

export default CountryTable;
